import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Badge
} from '@mui/material';
import DashboardLayout from '../components/DashboardLayout';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import { getOffersByCategories } from '../services/offerService';
import { useAuth } from '../context/AuthContext';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ArticleIcon from '@mui/icons-material/Article';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CategoryIcon from '@mui/icons-material/Category';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const StyledCard = styled(Card)(({ theme, expired }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  opacity: expired ? 0.7 : 1,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const CategoryCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  border: `1px solid ${theme.palette.divider}`,
}));

const SubcategoryHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1, 0),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const OfferCard = ({ offer }) => {
  const isExpired = dayjs().isAfter(dayjs(offer.expirationDate));
  
  return (
    <StyledCard expired={isExpired}>
      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography variant="h6" component="h3" gutterBottom>
            {offer.title}
          </Typography>
          {isExpired && (
            <Chip 
              label="Expired" 
              size="small" 
              color="error" 
            />
          )}
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {offer.description.length > 100 
            ? `${offer.description.substring(0, 100)}...` 
            : offer.description}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            {isExpired 
              ? `Expired ${dayjs(offer.expirationDate).format('MMM D, YYYY')}`
              : `Expires ${dayjs(offer.expirationDate).format('MMM D, YYYY')}`
            }
          </Typography>
        </Box>

        {offer.originalPrice && offer.finalPrice && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
              ${offer.originalPrice}
            </Typography>
            <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 'bold' }}>
              ${offer.finalPrice}
            </Typography>
            {offer.discountPercentage && (
              <Chip 
                label={`${offer.discountPercentage}% OFF`} 
                size="small" 
                color="success" 
                variant="outlined"
              />
            )}
          </Box>
        )}

        {offer.tags && offer.tags.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
            {offer.tags.slice(0, 3).map((tag, index) => (
              <Chip key={index} label={tag} size="small" variant="outlined" />
            ))}
            {offer.tags.length > 3 && (
              <Chip label={`+${offer.tags.length - 3}`} size="small" variant="outlined" />
            )}
          </Box>
        )}
      </CardContent>
      
      <CardActions sx={{ p: 2, pt: 0 }}>
        <Button
          component="a"
          href={offer.purchaseUrl}
          target="_blank"
          rel="noopener noreferrer"
          variant="contained"
          startIcon={<ShoppingCartIcon />}
          disabled={isExpired}
          sx={{ mr: 1 }}
          size="small"
        >
          {offer.callToAction || 'Get Offer'}
        </Button>
        
        {offer.blogLink && (
          <Button
            component="a"
            href={offer.blogLink}
            target="_blank"
            rel="noopener noreferrer"
            variant="outlined"
            startIcon={<ArticleIcon />}
            size="small"
          >
            Read More
          </Button>
        )}
        
        <Button
          component={RouterLink}
          to={`/offers/${offer.slug}`}
          variant="text"
          size="small"
          sx={{ ml: 'auto' }}
        >
          Details
        </Button>
      </CardActions>
    </StyledCard>
  );
};

const OffersByCategory = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [categoriesData, setCategoriesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set());

  useEffect(() => {
    fetchOffersByCategories();
  }, []);

  const fetchOffersByCategories = async () => {
    try {
      setLoading(true);
      const data = await getOffersByCategories();
      setCategoriesData(data);
      
      // Auto-expand categories that have offers
      const categoriesToExpand = new Set();
      data.forEach(category => {
        if (category.subcategories.length > 0) {
          categoriesToExpand.add(category._id);
        }
      });
      setExpandedCategories(categoriesToExpand);
      
      setError(null);
    } catch (err) {
      setError('Failed to fetch offers. Please try again later.');
      console.error('Error fetching offers by categories:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryToggle = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const getTotalOffersInCategory = (category) => {
    return category.subcategories.reduce((total, sub) => total + sub.totalOffers, 0);
  };

  if (loading) {
    return (
      <DashboardLayout title="Loading...">
        <Container sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Container>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Offers by Category">
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
            Browse offers organized by categories and subcategories
          </Typography>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="body1" color="text.secondary">
            {categoriesData.length} categories with active offers
          </Typography>
          
          {isAuthenticated && (
            <Button
              component={RouterLink}
              to="/offers/new"
              variant="contained"
              startIcon={<LocalOfferIcon />}
            >
              Create Offer
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {categoriesData.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <LocalOfferIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No offers available at the moment
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Check back later for exciting deals!
          </Typography>
        </Box>
      )}

      {categoriesData.map((category) => (
        <CategoryCard key={category._id}>
          <Accordion 
            expanded={expandedCategories.has(category._id)}
            onChange={() => handleCategoryToggle(category._id)}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ 
                backgroundColor: 'primary.light', 
                '&:hover': { backgroundColor: 'primary.dark' }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CategoryIcon sx={{ mr: 2, color: 'primary.main' }} />
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {category.name}
                  </Typography>
                </Box>
                <Badge 
                  badgeContent={getTotalOffersInCategory(category)} 
                  color="primary"
                  sx={{ mr: 2 }}
                >
                  <LocalOfferIcon />
                </Badge>
              </Box>
            </AccordionSummary>
            
            <AccordionDetails sx={{ p: 0 }}>
              {category.subcategories.map((subcategory) => (
                <Box key={subcategory._id} sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <SubcategoryHeader>
                    <ArrowForwardIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                      {subcategory.name}
                    </Typography>
                    <Chip 
                      label={`${subcategory.totalOffers} offers`}
                      size="small"
                      variant="outlined"
                    />
                    <Button
                      component={RouterLink}
                      to={`/offers/category/${category._id}/subcategory/${subcategory._id}`}
                      size="small"
                      sx={{ ml: 2 }}
                    >
                      View All
                    </Button>
                  </SubcategoryHeader>

                  <Grid container spacing={2}>
                    {subcategory.offers.map((offer) => (
                      <Grid item xs={12} sm={6} md={4} key={offer._id}>
                        <OfferCard offer={offer} />
                      </Grid>
                    ))}
                  </Grid>

                  {subcategory.totalOffers > subcategory.offers.length && (
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                      <Button
                        component={RouterLink}
                        to={`/offers/category/${category._id}/subcategory/${subcategory._id}`}
                        variant="outlined"
                      >
                        View {subcategory.totalOffers - subcategory.offers.length} More Offers
                      </Button>
                    </Box>
                  )}
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
        </CategoryCard>
      ))}
    </Container>
    </DashboardLayout>
  );
};

export default OffersByCategory; 